<script setup lang="ts" generic="T, Q extends Partial<T>">
import { computed, ref } from "vue";
import AgnosticValidationHandler from "@/shared/lib/validation/AgnosticValidationHandler";

import BaseInput from "@/shared/components/temparary/components/base/input/BaseInput.vue";
import BaseLabel from "@/shared/components/temparary/components/base/label/BaseLabel.vue";
import BaseButton from "@/shared/components/temparary/components/base/button/BaseButton.vue";

import type {
    FormInputProps,
    FormInputEmits,
} from "@/shared/components/temparary/components/formInput/FormInputModel";

const agnosticValidationHandler = new AgnosticValidationHandler();

const props = defineProps<FormInputProps<T, Q>>();
const emits = defineEmits<FormInputEmits<T, Q>>();

const typeRef = ref<string>(props.type);

const validationModelValue = computed(() => {
    if (props.validation.length === 0) return { valid: true, message: "" };
    const inputValue = props.modelValue;
    const invalidInputValue = agnosticValidationHandler.handler(inputValue, props.validation);
    // 해당 invalid value 에 따라 유효성 메시지를 전달합니다.
    // TODO: 타입 단언 부분을 수정해봐야 할 듯 합니다.
    emits("emitBaseInvalidInputValue", {
        name: props.name,
        value: invalidInputValue.message as Q[keyof Q],
    });

    return invalidInputValue;
});

const emitBaseInputValue = (value: string) => {
    emits("emitBaseInputValue", { name: props.name, value: value });
};

const emitValidationEvent = () => {
    emits("emitVerificationEvent");
};
</script>

<template>
    <div
        class="flex flex-col md:flex-row items-start md:items-center justify-start w-full h-auto md:gap-[2.4rem]"
    >
        <BaseLabel
            v-if="!isNotLabel"
            :name="props.name.toString()"
            :label="props.label"
            :required="required"
            class="xl:w-[15.6rem] w-[9rem] mb-[0.7rem] font-bold"
        />
        <div class="w-full">
            <div class="flex items-start justify-center w-full h-full gap-[0.2rem]">
                <div
                    class="flex sm:flex-row flex-col items-center justify-center w-full h-full gap-[1.2rem] sm:max-h-[4rem]"
                >
                    <BaseInput
                        :type="typeRef"
                        :value="modelValue"
                        @emit-input-value="emitBaseInputValue"
                        @keypress.enter="emitValidationEvent"
                        :required="true"
                        id="name"
                        :validation="validationModelValue?.valid"
                        :placeholder="placeholder"
                        :disabled="disabled"
                        :maxlength="maxlength"
                    />
                    <div class="contents h-auto w-fit">
                        <BaseButton
                            v-if="isVerification"
                            :value="isVerification"
                            :color="'normal'"
                            :disabled="buttonDisabled"
                            :type="'button'"
                            :loading="loading"
                            :responsive-mobile-width="true"
                            @click="emitValidationEvent"
                            @keyup.enter="emitValidationEvent"
                        />
                    </div>
                </div>
            </div>
            <p class="mt-2 text-sm text-primary-red">
                {{ modelValue && modelValue.length > 0 ? validationModelValue?.message : "" }}
            </p>
        </div>
    </div>
</template>
